// app-subdomain-creators/src/stores/presenceStore.js

import { defineStore } from 'pinia';

export const usePresenceStore = defineStore('presence', {
  state: () => ({
    memberPresence: {},
    creatorPresence: {},
  }),

  getters: {
    /**
     * Returns: 'online' | 'away' | 'offline'
     */
    memberStatus: (state) => (memberId) => {
      const key = String(memberId);
      const rec = state.memberPresence[key];
      if (!rec) return 'offline';

      const status = rec.status || 'offline';
      const lastSeenTs = rec.lastSeen ? Date.parse(rec.lastSeen) : NaN;

      // ✅ 1) Explicit statuses from backend win
      if (status === 'online') return 'online';
      if (status === 'offline') return 'offline';
      if (status === 'away') return 'away';

      // ✅ 2) (Optional) Fallback: derive "away" only if we somehow
      //      have a lastSeen but no explicit status (future-proofing)
      if (!Number.isNaN(lastSeenTs)) {
        const diffMs = Date.now() - lastSeenTs;
        if (diffMs <= 5 * 60 * 1000) {
          return 'away';
        }
      }

      return 'offline';
    },

    isMemberOnline() {
      return (memberId) => this.memberStatus(memberId) === 'online';
    },

    isMemberAway() {
      return (memberId) => this.memberStatus(memberId) === 'away';
    },
  },

  actions: {
    /**
     * Apply a bulk snapshot from backend:
     * results: [{ userId, status, lastSeen }]
     */
    applyMemberSnapshot(results, userType = 'member') {
      if (!Array.isArray(results) || userType !== 'member') return;

      results.forEach((item) => {
        const key = String(item.userId);
        this.memberPresence[key] = {
          status: item.status || 'offline',
          lastSeen: item.lastSeen || null,
        };
      });
    },

    /**
     * Apply a single presence delta.
     * payload: { userType, userId, status, lastSeen }
     */
    applyMemberUpdate(payload) {
      if (!payload || payload.userType !== 'member' || !payload.userId) return;

      const key = String(payload.userId);
      this.memberPresence[key] = {
        status: payload.status || 'offline',
        lastSeen: payload.lastSeen || null,
      };
    },
  },
});
